import { notificationDestination } from './notificationNavigation';
import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router';
import { CssVarsProvider, extendTheme } from '@mui/joy';
import { useContext, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import './App.css';

import LoginPage from './pages/LoginPage';

// ── Фотограф ───────────────────────────────────────────────────────────────────
import PhotographerDashboard from './pages/photographer/PhotographerDashboard';
import ChangeRequestPage     from './pages/photographer/ChangeRequestPage';
import ErrorBoundary         from './components/ErrorBoundary';
import MySalaryPage from './pages/photographer/MySalaryPage';
import MyFinesPage from './pages/photographer/MyFinesPage';

// ── Руководитель ───────────────────────────────────────────────────────────────
import ManagerDashboard   from './pages/manager/ManagerDashboard';
import AssignPage         from './pages/manager/AssignPage';
// import ChangeRequestsPage from './pages/manager/ChangeRequestsPage';  // входящие запросы
// import EmployeesPage      from './pages/manager/EmployeesPage';
// import LocationsPage      from './pages/manager/LocationsPage';
// import MessagesPage       from './pages/manager/MessagesPage';

import AppProvider, { AppContext } from './context';
import Toast from './components/Toast';
import ChangeRequestsPage from './pages/manager/ChangeRequestsPage';
import EmployeesPage from './pages/manager/EmployeesPage';
import LocationsPage from './pages/manager/LocationsPage';
import MessagesPage from './pages/manager/MessagesPage';
import FillRequestsPage from './pages/manager/FillRequestsPage';
import SubstitutionsPage from './pages/manager/SubstitutionsPage';
import OrdersPage    from './pages/manager/OrdersPage';
import AddOrderPage  from './pages/manager/AddOrderPage';
import StatsPage     from './pages/manager/StatsPage';
import SalaryPage    from './pages/manager/SalaryPage';
import SalaryConfigPage from './pages/manager/SalaryConfigPage';
import RewardsPage   from './pages/manager/RewardsPage';
import FinesPage    from './pages/manager/FinesPage';
import ReportPage   from './pages/manager/ReportPage';
import SettingsPage from './pages/manager/SettingsPage';
import RolesPage from './pages/manager/RolesPage';
import EmployeeFormPage from './pages/manager/EmployeeFormPage';
import HistoryPage from './pages/manager/HistoryPage';
import PendingSalesPage from './pages/manager/PendingSalesPage';
import PaymentNumbersPage from './pages/manager/PaymentNumbersPage';
import TrashedEmployeesPage from './pages/manager/TrashedEmployeesPage';
import ProductsDiscountsPage from './pages/manager/ProductsDiscountsPage';
import WriteOffRequestsPage from './pages/manager/WriteOffRequestsPage';
import OrderDeletionRequestsPage from './pages/manager/OrderDeletionRequestsPage';

import WorkstationDashboard  from './pages/workstation/WorkstationDashboard';
import WorkstationOrdersPage from './pages/workstation/WorkstationOrdersPage';
import WorkstationFinesPage  from './pages/workstation/WorkstationFinesPage';
import WorkstationStatsPage  from './pages/workstation/WorkstationStatsPage';
import ReassignRequestsWatcher from './pages/workstation/ReassignRequestsWatcher';

// Для руководителя:
import WorkstationsManagePage  from './pages/manager/WorkstationsManagePage';
// import AdministratorsPage      from './pages/manager/AdministratorsPage';
// import ShiftHistoryPage        from './pages/manager/ShiftHistoryPage';


// ─── MUI Joy тема ─────────────────────────────────────────────────────────────
const theme = extendTheme({
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    50:  '#f7eaff',
                    100: '#ecccff',
                    200: '#da9eff',
                    300: '#c56ffa',
                    400: '#b24aef',
                    500: '#A226E2',
                    600: '#8a1fc9',
                    700: '#7218a8',
                    800: '#591287',
                    900: '#3f0c60',
                },
                background: {
                    body:    '#08080f',
                    surface: '#0f0f1a',
                    popup:   '#13131f',
                    level1:  '#13131f',
                    level2:  '#18182a',
                    level3:  '#1e1e35',
                },
                text: {
                    primary:   '#e2e2f0',
                    secondary: '#9898b8',
                    tertiary:  '#5a5a7a',
                },
                neutral: {
                    500: '#3a3a5a',
                    600: '#2e2e4a',
                    700: '#22223a',
                },
                divider: 'rgba(162,38,226,0.14)',
            },
        },
    },
    defaultColorScheme: 'light',
    fontFamily: {
        body:    "'DM Sans', 'Inter', sans-serif",
        display: "'DM Sans', 'Inter', sans-serif",
        code:    "'JetBrains Mono', monospace",
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            
            extrasmall: 300,
            small: 500,
            medium: 800,
            large: 1100,
        },
    },
    components: {
        JoyButton: {
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                    const semantic = { danger: ['#ef4444', '#fca5a5'], success: ['#22c55e', '#86efac'], warning: ['#f59e0b', '#fcd34d'] }[ownerState.color];
                    return {
                        transition: '.2s', borderRadius: theme.vars.radius.lg, fontSize: 13,
                        backgroundColor: semantic ? `${semantic[0]}25` : 'var(--joy-palette-background-level2)',
                        border: `1px solid ${semantic ? `${semantic[0]}70` : 'var(--joy-palette-divider)'}`,
                        color: semantic ? semantic[1] : 'var(--joy-palette-text-primary)',
                        '&:hover': { backgroundColor: semantic ? `${semantic[0]}40` : 'var(--joy-palette-background-level3)' },
                        '&.Mui-disabled': { color: 'var(--joy-palette-text-tertiary)', backgroundColor: 'var(--joy-palette-background-level1)', borderColor: 'var(--joy-palette-divider)' },
                    };
                },
            },
        },
        JoyTextarea: {
            defaultProps: {
                sx: {
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                }
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.vars.radius.lg,
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                })
            }
        },
        JoyInput: {
            defaultProps: {
                sx: {
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                }
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.vars.radius.lg,
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                })
            }
        },
        JoySelect: {
            defaultProps: {
                sx: {
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        backgroundColor: 'var(--joy-palette-background-level3) !important', 
                    }
                },
                slotProps: {
                    listbox: {
                        sx: {
                            bgcolor: 'var(--joy-palette-background-level2)',
                            border: '1px solid var(--joy-palette-divider)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                            '& .MuiOption-highlighted': {
                                bgcolor: 'rgba(162,38,226,0.12) !important',
                                color: 'var(--joy-palette-text-primary)',
                            },
                            '& .MuiOption-root': {
                                color: 'var(--joy-palette-text-primary)',
                                fontSize: 13,
                                '&:hover': {
                                    bgcolor: 'rgba(162,38,226,0.12)',
                                    color: 'var(--joy-palette-text-primary)',
                                },
                                '&[aria-selected="true"]': {
                                    bgcolor: 'rgba(162,38,226,0.2)',
                                    color: 'var(--joy-palette-primary-300)',
                                    fontWeight: 600,
                                },
                            },
                        },
                    },
                }
            },
            styleOverrides: {
                listbox: { bgcolor: "var(--joy-palette-background-popup)", color: "var(--joy-palette-text-primary)", border: "1px solid var(--joy-palette-divider)", zIndex: 1500 },
                root: ({ theme }) => ({
                    borderRadius: theme.vars.radius.lg,
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        backgroundColor: 'var(--joy-palette-background-level3) !important', 
                    }
                })
            }
        },
        JoyAutocomplete: {
            defaultProps: {
                sx: {
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                }
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.vars.radius.lg,
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white', flex: 1,
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                })
            }
        },
        JoyIconButton: {
            defaultProps: {
                sx: {
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white',
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                }
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.vars.radius.lg,
                    bgcolor: 'var(--joy-palette-background-level2)',
                    border: '1px solid var(--joy-palette-divider)',
                    fontSize: 13, color: 'white',
                    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                    "&:hover": {
                        bgcolor: 'var(--joy-palette-background-level3)', 
                    }
                })
            }
        },
        JoyOption: {
            styleOverrides: {
                root: {
                    color: '#eaeaf5',
                    fontSize: 13,
                    '&:hover': {
                        backgroundColor: 'rgba(162,38,226,0.16)',
                        color: '#ffffff',
                    },
                    '&.Mui-selected, &[aria-selected="true"]': {
                        backgroundColor: 'rgba(162,38,226,0.22)',
                        color: 'var(--joy-palette-primary-300)',
                        fontWeight: 600,
                    },
                    '&.MuiOption-highlighted, &.Mui-highlighted': {
                        backgroundColor: 'rgba(162,38,226,0.16)',
                        color: '#ffffff',
                    },
                },
            },
        },
    },
});

// ─── Защищённый роут (любой авторизованный) ──────────────────────────────────
function PrivateRoute({ children }) {
    const { lock, loadInitial, account } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role === 'workstation') return <Navigate to="/workstation" replace />;
    return children;
}

// ─── Роут только для руководителя ────────────────────────────────────────────
function ManagerRoute({ children, requirePerm = null, requireAnyPerm = null }) {
    const { lock, loadInitial, account, hasPerm, hasAnyPerm, hasManagerCapabilities } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role === 'workstation') return <Navigate to="/workstation" replace />;

    // requireAnyPerm (массив) — доступ, если есть ЛЮБОЕ из прав (как в меню).
    // requirePerm (одно право) — совместимость. Без обоих — любое менеджерское право.
    const hasAccess = requireAnyPerm
        ? hasAnyPerm(...requireAnyPerm)
        : requirePerm
            ? hasPerm(requirePerm)
            : hasManagerCapabilities();

    if (!hasAccess) return <Navigate to="/" replace />;
    return children;
}

// ─── Публичный роут (редирект если уже вошёл) ────────────────────────────────
function PublicRoute({ children }) {
    const { lock, loadInitial } = useContext(AppContext);
    if (loadInitial) return null;
    return !lock ? <Navigate to="/" replace /> : children;
}

function WorkstationRoute({ children }) {
    const { lock, loadInitial, account } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role !== 'workstation') return <Navigate to="/" replace />;
    return (
        <>
            {children}
            <ReassignRequestsWatcher />
        </>
    );
}

// ─── Умный редирект с "/" в зависимости от роли ──────────────────────────────
function RoleRedirect() {
    const { lock, loadInitial, account, landsOnManagerDashboard } = useContext(AppContext);
    if (loadInitial) return null;
    if (lock) return <Navigate to="/login" replace />;
    if (account?.role === 'workstation') return <Navigate to="/workstation" replace />;
    if (landsOnManagerDashboard())       return <Navigate to="/manager" replace />;
    return <PhotographerDashboard />;
}

function NotificationMoneyRoute({ kind, children }) {
    const { account, loadInitial } = useContext(AppContext);
    if (loadInitial) return null;
    const destination = notificationDestination({ notifType: kind }, account);
    const personal = kind === 'fine' ? '/my-fines' : '/my-salary';
    return destination !== personal ? <Navigate to={destination} replace /> : children;
}

// ─── Роуты ────────────────────────────────────────────────────────────────────
function AppRoutes() {
    const location = useLocation();

    useEffect(() => {
        const sw = navigator.serviceWorker?.controller;
        const notify = (type) => sw?.postMessage({ type });
        notify('site_active');
        const onVisible = () => notify('site_active');
        const onHidden  = () => notify('site_inactive');
        const onVisibilityChange = () => document.hidden ? onHidden() : onVisible();
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('beforeunload', onHidden);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('beforeunload', onHidden);
        };
    }, []);

    return (
        <>
        <AnimatePresence initial={false} mode="sync">
            <Routes location={location} key={location.pathname}>

                {/* ── Публичные ──────────────────────────────────────────── */}
                <Route path="/login" element={
                    <PublicRoute><LoginPage /></PublicRoute>
                } />

                {/* ── Умный редирект: менеджер → /manager, фотограф → Dashboard ── */}
                <Route path="/" element={<RoleRedirect />} />

                {/* ── Фотограф ───────────────────────────────────────────── */}
                <Route path="/change-request" element={
                    <PrivateRoute><ChangeRequestPage /></PrivateRoute>
                } />
                <Route path="/my-salary" element={
                    <PrivateRoute><NotificationMoneyRoute kind="reward"><MySalaryPage /></NotificationMoneyRoute></PrivateRoute>
                } />
                <Route path="/my-fines" element={
                    <PrivateRoute><NotificationMoneyRoute kind="fine"><MyFinesPage /></NotificationMoneyRoute></PrivateRoute>
                } />
                <Route path="/my-schedule" element={
                    <PrivateRoute><PhotographerDashboard /></PrivateRoute>
                } />

                {/* ── Руководитель ───────────────────────────────────────── */}
                <Route path="/manager" element={
                    <ManagerRoute><ManagerDashboard /></ManagerRoute>
                } />
                <Route path="/manager/assign" element={
                    <ManagerRoute requireAnyPerm={['schedule.view']}><AssignPage /></ManagerRoute>
                } />
                <Route path="/manager/change-requests" element={
                    <ManagerRoute requirePerm="schedule.change_requests.review"><ChangeRequestsPage /></ManagerRoute>
                } />
                <Route path="/manager/write-offs" element={
                    <ManagerRoute requirePerm="finance.stock.writeoff.review"><WriteOffRequestsPage /></ManagerRoute>
                } />
                <Route path="/manager/order-deletion-requests" element={
                    <ManagerRoute requirePerm="finance.orders.delete_requests.review"><OrderDeletionRequestsPage /></ManagerRoute>
                } />
                <Route path="/manager/employees" element={
                    <ManagerRoute requireAnyPerm={['employees.view', 'employees.create', 'employees.update', 'employees.fire', 'employees.restore', 'employees.delete', 'employees.reset_password']}><EmployeesPage /></ManagerRoute>
                } />
                <Route path="/manager/employees/new" element={
                    <ManagerRoute requirePerm="employees.create"><EmployeeFormPage /></ManagerRoute>
                } />
                <Route path="/manager/employees/:id/edit" element={
                    <ManagerRoute requirePerm="employees.update"><EmployeeFormPage /></ManagerRoute>
                } />
                <Route path="/manager/roles" element={
                    <ManagerRoute requirePerm="roles.view"><RolesPage /></ManagerRoute>
                } />
                <Route path="/manager/locations" element={
                    <ManagerRoute requireAnyPerm={['locations.create', 'locations.update', 'locations.delete', 'locations.change_default']}><LocationsPage /></ManagerRoute>
                } />
                <Route path="/manager/messages" element={
                    <ManagerRoute requireAnyPerm={['system.messages.view', 'system.messages.create']}><MessagesPage /></ManagerRoute>
                } />
                <Route path="/manager/fill-requests" element={
                    <ManagerRoute requirePerm="schedule.fill_requests.review"><FillRequestsPage /></ManagerRoute>
                } />
                <Route path="/manager/substitutions" element={
                    <ManagerRoute requireAnyPerm={['schedule.substitutions.review', 'schedule.substitutions.view']}><SubstitutionsPage /></ManagerRoute>
                } />
                <Route path="/manager/orders" element={
                    <ManagerRoute requirePerm="finance.orders.create"><OrdersPage /></ManagerRoute>
                } />
                <Route path="/manager/orders/add" element={
                    <ManagerRoute requirePerm="finance.orders.create"><AddOrderPage /></ManagerRoute>
                } />
                <Route path="/manager/pending-sales" element={
                    <ManagerRoute requirePerm="finance.sale_notifications.review"><PendingSalesPage /></ManagerRoute>
                } />
                <Route path="/manager/stats" element={
                    <ManagerRoute requireAnyPerm={['reports.stats', 'reports.view']}><StatsPage /></ManagerRoute>
                } />
                <Route path="/manager/salary-report" element={
                    <ManagerRoute requirePerm="reports.view"><SalaryPage /></ManagerRoute>
                } />
                <Route path="/manager/salary" element={
                    <ManagerRoute requirePerm="finance.configs.view"><SalaryConfigPage /></ManagerRoute>
                } />
                <Route path="/manager/rewards" element={
                    <ManagerRoute requireAnyPerm={['finance.rewards.view', 'finance.rewards.create']}><RewardsPage /></ManagerRoute>
                } />
                <Route path="/manager/fines" element={
                    <ManagerRoute requireAnyPerm={['finance.fines.view', 'finance.fines.create']}><FinesPage /></ManagerRoute>
                } />
                <Route path="/manager/report" element={
                    <ManagerRoute requirePerm="reports.view"><ReportPage /></ManagerRoute>
                } />
                <Route path="/manager/workstations" element={
                    <ManagerRoute requireAnyPerm={['employees.manage_admins']}><WorkstationsManagePage /></ManagerRoute>
                } />
                <Route path="/manager/history" element={
                    <ManagerRoute requirePerm="events.view"><HistoryPage /></ManagerRoute>
                } />
                <Route path="/manager/payment-numbers" element={
                    <ManagerRoute requireAnyPerm={['payment_numbers.view']}><PaymentNumbersPage /></ManagerRoute>
                } />
                <Route path="/employees/trashed" 
                    element={<TrashedEmployeesPage />} 
                />
                <Route path="/products" element={
                    <ManagerRoute requireAnyPerm={['finance.products.view']}><ProductsDiscountsPage /></ManagerRoute>
                } />
                <Route path="/manager/settings" element={
                    <ManagerRoute><SettingsPage /></ManagerRoute>
                } />

                {/* ── Workstation ──────────────────────────────────────── */}
                <Route path="/workstation" element={
                    <WorkstationRoute><WorkstationDashboard /></WorkstationRoute>
                } />
                <Route path="/workstation/orders" element={
                    <WorkstationRoute><WorkstationOrdersPage /></WorkstationRoute>
                } />
                <Route path="/workstation/fines" element={
                    <WorkstationRoute><WorkstationFinesPage /></WorkstationRoute>
                } />
                <Route path="/workstation/stats" element={
                    <WorkstationRoute><WorkstationStatsPage /></WorkstationRoute>
                } />

                {/* ── Руководитель: новые ──────────────────────────────── */}
                {/* <Route path="/manager/administrators" element={
                    <ManagerRoute><AdministratorsPage /></ManagerRoute>
                } />
                <Route path="/manager/shift-history" element={
                    <ManagerRoute><ShiftHistoryPage /></ManagerRoute>
                } />

                {/* ── Фоллбэк ────────────────────────────────────────────── */}
                <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
        </AnimatePresence>
        <Toast />
        </>
    );
}

export default function App() {
    return (
        <CssVarsProvider 
            theme={theme} 
            defaultMode="light"
            modeStorageKey="massage-site-theme"
            disableTransitionOnChange
        >
            <BrowserRouter>
                <AppProvider>
                    <ErrorBoundary>
                        <AppRoutes />
                    </ErrorBoundary>
                </AppProvider>
            </BrowserRouter>
        </CssVarsProvider>
    );
}